import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll, ContentImageSection } from "../../components/sections";
import { FiTarget, FiCloud, FiZap, FiHardDrive, FiBarChart2, FiSettings, FiTrendingUp, FiDollarSign, FiArrowRight, FiSmile } from 'react-icons/fi';
import bgImage from "../../images/img1.jpg";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const DigitalTransformationPage = () => {
  usePageMetadata({
    title: 'Digital Transformation - Bercastle',
    description: 'Strategy & roadmap for digital transformation with cloud migration, process automation, and data analytics.',
    pageType: 'service',
    content: {
      service: {
        name: "Digital Transformation",
        tagline: "Transform Your Business for the Digital Age",
        description: "Comprehensive digital transformation strategy and implementation to modernize operations and drive growth.",
        url: "/services/digital-transformation"
      },
      capabilities: [
        {
          name: "Strategy & Roadmap",
          description: "Develop a clear digital transformation strategy aligned with business goals.",
          planning: ["Current state assessment", "Vision & goal setting", "Technology roadmap", "Change management plan", "ROI analysis", "Implementation timeline"]
        },
        {
          name: "Cloud Migration",
          description: "Move infrastructure and applications to the cloud for increased agility.",
          services: ["Cloud readiness assessment", "Migration strategy", "Application modernization", "Data migration", "Security & compliance"]
        },
        {
          name: "Process Automation",
          description: "Automate manual processes to increase efficiency and reduce errors.",
          solutions: ["Robotic Process Automation (RPA)", "Workflow automation", "Document processing", "Business rules engines", "AI-powered automation"]
        },
        {
          name: "Data & Analytics",
          description: "Harness your data for better decision-making and business insights.",
          services: ["Data warehouse design", "Business intelligence dashboards", "Predictive analytics", "Real-time reporting", "Data governance"]
        },
        {
          name: "Legacy System Modernization",
          description: "Update outdated systems while preserving critical business logic.",
          approaches: ["Replatforming", "Refactoring", "Rebuilding", "API integration", "Microservices migration"]
        }
      ],
      benefits: ["Increased Efficiency", "Better Decisions", "Cost Reduction", "Improved Experience", "Competitive Advantage", "Scalability"],
      methodology: ["Assess", "Strategize", "Plan", "Execute", "Optimize", "Scale"],
      value_propositions: [
        "Holistic approach",
        "Proven methodologies",
        "Change management expertise",
        "Technology agnostic",
        "Business-focused outcomes",
        "Long-term partnership"
      ],
      meta: {
        source_url: "https://bercastle.com/services/digital-transformation",
        service_category: "Digital Transformation"
      }
    }
  });
  const transformationServices = [
    {
      icon: <FiTarget size={24} />,
      title: 'Strategy & Roadmap',
      description: 'Develop comprehensive digital transformation strategy aligned with business objectives and market trends.'
    },
    {
      icon: <FiCloud size={24} />,
      title: 'Cloud Migration',
      description: 'Seamlessly migrate legacy systems to modern cloud platforms for improved scalability and cost efficiency.'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Process Automation',
      description: 'Automate manual workflows with AI and intelligent automation to increase efficiency and reduce errors.'
    },
    {
      icon: <FiHardDrive size={24} />,
      title: 'Legacy Modernization',
      description: 'Transform outdated systems into modern, maintainable applications using latest technologies.'
    },
    {
      icon: <FiBarChart2 size={24} />,
      title: 'Data & Analytics',
      description: 'Implement data-driven decision making with advanced analytics and business intelligence solutions.'
    },
    {
      icon: <FiSettings size={24} />,
      title: 'Change Management',
      description: 'Guide your team through transformation with training, support, and cultural change initiatives.'
    }
  ];

  const benefits = [
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Increased Efficiency',
      description: 'Automate processes and eliminate bottlenecks'
    },
    {
      icon: <FiDollarSign size={24} />,
      title: 'Cost Reduction',
      description: 'Lower operational costs through optimization'
    },
    {
      icon: <FiArrowRight size={24} />,
      title: 'Faster Innovation',
      description: 'Rapid deployment of new features and services'
    },
    {
      icon: <FiSmile size={24} />,
      title: 'Better CX',
      description: 'Enhanced customer experience and satisfaction'
    }
  ];

  return (
    <Layout classmain="digital-transformation-page">
      <Seo
        title='Digital Transformation - OnScript AI'
        metaDesciption='Drive innovation and efficiency through tailored digital transformation strategies. Modernize your business with AI-powered solutions and cloud technologies.'
      />
      <Header />

      <ServiceHero 
        badge="Digital Transformation"
        title="Modernize Your Business"
        titleHighlight="With AI & Cloud"
        description="Drive innovation and efficiency through tailored digital transformation strategies that align with your business goals."
        ctaLink="/contact"
      />

      {/* What is Digital Transformation */}
      <div className='py-20'>
        <div className='container-custom'>
          <ContentImageSection
            image={bgImage}
            imageAlt='Digital Transformation'
          >
            <div>
              <h2 className='text-4xl md:text-5xl font-bold mb-6 text-slate-900'>What is Digital Transformation?</h2>
              <p className='text-xl text-slate-600 leading-relaxed mb-6'>
                Digital transformation is the process of using digital technologies to fundamentally change how your 
                business operates and delivers value to customers. It's about modernizing systems, automating processes, 
                and leveraging AI and cloud technologies to stay competitive in a rapidly evolving market.
              </p>
              <p className='text-xl text-slate-600 leading-relaxed'>
                We help organizations navigate this journey with strategic planning, cutting-edge technology, 
                and proven methodologies.
              </p>
            </div>
          </ContentImageSection>
        </div>
      </div>

      {/* Services Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Transformation Services"
            description="Comprehensive services to modernize every aspect of your business"
            cards={transformationServices}
            cols={3}
            className='w-full'
          />
        </div>
      </div>

      {/* Benefits Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <HorizontalScroll
            title="Transformation Benefits"
            description="Real results from digital transformation done right"
            items={benefits}
            cardWidth={280}
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Transform Your Business?"
        description="Let's build a digital transformation strategy that drives real results for your organization."
        buttonText="Schedule a Consultation"
      />

      <Footer />
    </Layout>
  )
}

export default DigitalTransformationPage
